const post = {
  meta: {
    slug: "how-mca-factor-rates-work",
    title: "How MCA Factor Rates Really Work: The Math, in Plain English",
    excerpt:
      "Factor rates aren't APR. Here's exactly what they are, how the payback math works, how to translate a factor into an honest APR — and the two contract clauses that decide whether the deal is fair.",
    date: "2026-01-14",
    readTime: "8 min read",
    category: "Buyer's guide",
    image:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## Factor rate: the definition, in one line

A **factor rate** is the multiplier applied to the advance amount to determine the total payback. A 1.35 factor on a $100,000 advance means you'll pay back $135,000. That's it.

- Advance: $100,000
- Factor: 1.35
- Payback amount: $100,000 × 1.35 = **$135,000**
- Total fee: $35,000

Factor rates don't compound. They don't accrue over time like interest. The full $35,000 fee is baked in from day one — regardless of whether you pay it back in 6 months or 18 months.

That's what makes them dangerous. And that's what we'll unpack in this article.

## Why the industry uses factor rates instead of APR

Two reasons — one technical, one marketing.

**Technical:** An MCA is technically not a loan. It's the sale of future revenue at a discount. Because there's no fixed term (repayment happens over whatever period it takes to hit the payback amount), you can't calculate a traditional APR without knowing the actual payoff speed. Factor rate sidesteps the problem.

**Marketing:** Factor rates *look* smaller than APR. A 1.35 factor "sounds like" 35% — an ugly but understandable number. The actual APR of that same deal is often 60–130%. Which number would you rather quote in a sales call?

For the borrower's protection, always translate factor to APR before signing. Here's how.

## The factor-to-APR conversion (with worked examples)

The exact formula for converting a factor rate to an effective APR depends on the payback schedule. But here's a good approximation for daily-debit MCAs:

**Approximate APR ≈ (Factor − 1) × (365 / Payback days) × 100%**

### Example 1: $100K advance, 1.35 factor, 12-month payback

- Total fee: $35,000
- Days to repay: ~365
- APR ≈ 0.35 × (365/365) × 100% = **35% APR** *(if you took the full 12 months)*

That's already deceptively low because we haven't accounted for the **declining balance** — since you're paying every day, your average outstanding balance over the year is roughly half the advance. The real math:

- Average outstanding balance: ~$50,000
- Total fee: $35,000
- Approximate real APR: 35,000 / 50,000 = **70% APR**

### Example 2: $100K advance, 1.35 factor, 8-month payback

- Days to repay: ~240
- Approximate APR ≈ 0.35 × (365/240) × 100% = **53% quoted**
- Adjusted for declining balance: **~106% real APR**

### Example 3: $100K advance, 1.35 factor, 6-month payback

- Days to repay: ~180
- Approximate APR ≈ 0.35 × (365/180) × 100% = **71% quoted**
- Adjusted for declining balance: **~142% real APR**

**The critical insight**: the same factor rate produces wildly different APRs depending on payback speed. Faster payback = higher APR. This is the opposite of how a traditional loan works, where paying early saves you money.

## The two things that actually control the payback speed

MCAs are structured one of two ways. The structure decides how fast you repay — and therefore your effective APR.

### Structure #1: Fixed daily/weekly ACH debit

Most modern MCAs work this way. The funder debits a fixed dollar amount from your operating account every business day (or weekly) until the total payback is reached.

- Advance: $100,000
- Payback: $135,000
- Daily debit: $675 (5 days/week × ~40 weeks = 200 business days)

The payback speed is fixed by contract. Your business's revenue can go up or down, but the debit stays the same. If revenue slows, the debit hurts more.

### Structure #2: True revenue-percentage remit

The older MCA model, still used by some funders. The remit is a fixed **percentage** of your daily card sales — typically 8–20%. When sales are strong, you repay faster. When sales slow, you repay slower.

- Advance: $100,000
- Payback: $135,000
- Split: 12% of daily card sales

This structure is genuinely more forgiving in slow months — but funders don't offer it as often anymore because it makes their return unpredictable.

## The two contract clauses that make or break the deal

If you're going to take an MCA, the entire economics of the deal come down to two clauses in the contract. Read these before signing anything else.

### Clause #1: The prepayment discount

Because there's no time-based interest, paying an MCA off early doesn't automatically save you money — the full payback amount is contractually owed. **Unless the contract has a prepayment discount.**

A real prepayment discount looks like: "If prepaid within 90 days, remaining fee reduced by 40%." That's a genuine early-payoff incentive that can cut your effective cost meaningfully.

A **fake** prepayment discount looks like: "If prepaid, remaining daily debits due immediately." That's not a discount — that's just calling in the remaining debt.

**Ask the funder for a specific early-payoff quote as of day 30, day 60, day 90.** If they can't produce one, or if the numbers don't materially reduce the total, you don't actually have a prepayment discount.

### Clause #2: The reconciliation clause

If revenue drops significantly, does the daily debit adjust down?

- A **reconciliation clause** allows the debit to be recalculated (usually monthly) based on updated deposit averages. Genuinely borrower-protective.
- **No reconciliation clause** means the debit stays the same regardless of your revenue reality. If you have a bad month, you're on the hook for the same daily payment.

Aggressive MCA funders often either omit reconciliation entirely or make it so procedurally difficult that borrowers can't actually invoke it. Ask specifically: *"How is a reconciliation requested, and how quickly is it processed?"*

## The prepayment example

$100,000 advance, 1.35 factor, 12-month contract. Business booms in month 3 and you want to pay off early.

- **With no prepayment discount:** you owe the full $135,000 minus what you've paid so far. If you've paid $50,000 in daily debits, you'd need to send $85,000 to close it out. Effective APR: ~140% (much worse than the "12-month APR" you were quoted).
- **With a 40% remaining-fee prepayment discount:** the remaining $35,000 payback fee gets reduced by 40% → $21,000 in remaining fees. Total remaining payoff: $85,000 principal already spent + adjusted balance ≈ $70,000. Effective APR: ~55%.

Same deal on paper. Wildly different economics based on one paragraph in the contract.

## The five questions to ask any MCA funder

1. **"What is the effective APR on this advance based on the scheduled payback speed?"** — Get a real number.
2. **"Is there a prepayment discount, and what does it look like as of day 30, 60, and 90?"** — Get the exact numbers.
3. **"Is there a reconciliation clause?"** — Get the clause quoted to you.
4. **"How is a reconciliation requested — what documentation is required?"** — If the answer is "case by case," it means "no."
5. **"Will this advance be stacked on top of any existing advance?"** — Stacked advances kill businesses. If yes, walk away.

## The bottom line

Factor rates aren't inherently predatory. They're a legitimate pricing structure for a legitimate product — fast, opportunity-driven capital that would otherwise be unavailable to businesses with imperfect credit.

But because the same factor produces wildly different APRs depending on payback speed and contract terms, the difference between a fair MCA and a punishing one comes down to specific numbers most brokers never volunteer.

At Steady Path Funding, we translate factor rates to APR on every MCA we broker. We quote the effective rate. We negotiate real prepayment discounts. We won't stack you. That's the difference between a broker and a salesperson.

[See what you qualify for](/apply) — soft credit pull, no upfront fees, honest math.

Related: [The true cost of a merchant cash advance](/blog/true-cost-of-merchant-cash-advance) · [MCA vs. line of credit](/blog/mca-vs-line-of-credit)
`,
};

export default post;
